import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { navigate } from '@reach/router';
import { useMutation } from '@apollo/react-hooks';
import { Button, FormErrors } from '@squirrellearningltd/go-pupil-components';
import TextareaAutosize from 'react-textarea-autosize';
import RatingOptions from '../RatingOptions';
import { ADD_READING_BOOK_REVIEW, STUDENT_DONT_ADD_REVIEW } from '../../../../../../services/graphql';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import styles from './ReviewForm.module.scss';

const ReviewForm = ({ studentBookId, bookId }) => {

    const { t } = useTranslation();
    const [formErrors, setFormErrors] = useState([]);
    const [rating, setRating] = useState(0);
    const [tempRating, setTempRating] = useState();
    const [reviewText, setReviewText] = useState('');

    const [addReadingBookReview, { loading }] = useMutation(ADD_READING_BOOK_REVIEW, {
        update: (_, { data }) => {
            if (data.addReadingBookReview.errors && data.addReadingBookReview.errors.length !== 0) {
                setFormErrors(data.addReadingBookReview.errors);
                return;
            }
        }
    });

    const [dontAddReadingBookReview, { loading: dontAddLoading }] = useMutation(STUDENT_DONT_ADD_REVIEW, {
        update: (_, { data }) => {
            if (data.dontAddReadingBookReview.errors && data.dontAddReadingBookReview.errors.length !== 0) {
                setFormErrors(data.dontAddReadingBookReview.errors);
                return;
            }
            if (data.dontAddReadingBookReview.studentReadingBook.id) {
                navigate('/')
            }
        }
    });

    const schema = Yup.object().shape({
        text: Yup.string().required(t('please_enter_your_review')),
        rate: Yup.number().min(1, t('please_choose_rating'))
    });

    const { register, handleSubmit, errors } = useForm({
        validationSchema: schema
    });

    const onSubmit = values => {
        const { rate, text } = values;
        addReadingBookReview({
            variables: {
                bookId,
                rate,
                text
            }
        })
    }

    return(
        <form className={styles.reviewForm} onSubmit={handleSubmit(onSubmit)}>
            <p className={cx(styles.reviewFormPrompt, styles.reviewFormRatingPrompt)}>{t('how_many_stars_do_you_give_this_book')}</p>
            <RatingOptions
                hideLabel
                rating={rating}
                tempRating={tempRating}
                setRating={setRating}
                setTempRating={setTempRating}
            />
            <input
                type="hidden"
                name="rate"
                ref={register}
                value={rating}
                readOnly
            />
            {errors.rate && <p className="basic-form__hint">{errors.rate.message}</p>}

            <p className={styles.reviewFormPrompt}>{t('write_your_review')}</p>
            <div className={cx('basic-form__group', styles.reviewFormThoughtsGroup)}>
                {!reviewText && (
                    <span aria-hidden="true" className={styles.reviewFormThoughtsPlaceholder}>
                        {t('give_us_your_thoughts')}
                    </span>
                )}
                <TextareaAutosize
                    aria-label={t('give_us_your_thoughts')}
                    className={cx('basic-form__text-box', styles.reviewFormThoughts)}
                    name="text"
                    inputRef={register}
                    minRows={4}
                    onChange={event => setReviewText(event.target.value)}
                />
            </div>
            {errors.text && <p className="basic-form__hint">{errors.text.message}</p>}
            {formErrors.length !== 0 && <FormErrors errors={formErrors.map(error => t(error))} />}
            <div className={styles.reviewFormActions}>
                <Button className={styles.reviewFormSubmit} type="submit" primary>{loading ? t('saving') : t('submit_review')}</Button>
                <Button className={styles.reviewFormSkip} type="button" outline onClick={() => dontAddReadingBookReview({variables: {studentReadingBookId: studentBookId}})}>{dontAddLoading ? t('saving') : t('no_thanks')}</Button>
            </div>
        </form>
    )
}

export default ReviewForm;
